/*
 * Lily & Minx - help pages: Contact and policies (lm-help)
 * LAST UPDATED: 29/09/2026
 * ---------------------------------------------------------------------------
 * Progressive enhancement for the lm-help sections. Every page reads and works
 * without it. Loaded as a module (deferred) by the sections that use it.
 *
 * - <lm-policy-page> (sections/lm-policy.liquid): a contents link, a link in a
 *   section's text or a URL hash that points at a policy section opens that
 *   <details> and moves focus to its summary, so keyboard and screen reader
 *   users land on the section they asked for. From 1000px, where the contents
 *   are pinned beside the text, it marks the entry being read with
 *   aria-current="true" (lm-help.css draws a rule beside it), using
 *   IntersectionObserver; below 1000px, or without it, nothing is marked.
 * - <lm-cookie-preferences> (Privacy only): shows its button when the page
 *   has the site's own cookie banner (<lm-consent>, module lm-consent) and
 *   opens its Cookie settings with the lm:consent:open event. Without it, the
 *   button shows only when Shopify's cookie banner API
 *   (window.privacyBanner.showPreferences) exists, and opens that banner's
 *   preferences. With neither, it stays hidden.
 * - <lm-contact-form> (sections/lm-contact.liquid and lm-contact-form.liquid,
 *   around snippets/lm-help-contact-form.liquid): a link such as
 *   /pages/contact?topic=return#contact-form preselects the topic whose line
 *   has that code, only when such a topic exists and the visitor has not
 *   picked one yet; after a post, focus moves to the success or error notice
 *   so screen readers announce it. Shopify only echoes email and message after
 *   a failed post, so the topic, order number and name are kept in
 *   sessionStorage for this tab when the form is sent, put back when the error
 *   notice shows, and removed on any other load (WCAG 3.3.7 Redundant Entry).
 *   The form still posts natively (hCaptcha).
 *
 * Each element is defined once, cleans up when removed, and works again after
 * the theme editor re-renders a section.
 */

/**
 * The element a "#anchor" points at, if it is inside root.
 * @param {HTMLElement} root
 * @param {string | null} hash
 * @returns {HTMLElement | null}
 */
const targetForHash = (root, hash) => {
  if (!hash || !hash.startsWith('#') || hash.length < 2) return null;

  let id;
  try {
    id = decodeURIComponent(hash.slice(1));
  } catch {
    return null;
  }

  const target = document.getElementById(id);
  return target && root.contains(target) ? target : null;
};

/**
 * The policy section a "#anchor" points at, if it is a <details> inside root.
 * @param {HTMLElement} root
 * @param {string | null} hash
 * @returns {HTMLDetailsElement | null}
 */
const detailsForHash = (root, hash) => {
  const target = targetForHash(root, hash);
  return target instanceof HTMLDetailsElement ? target : null;
};

/* The policy's desktop layout (contents pinned beside the text) starts at
   Prestige's 1000px breakpoint, as in lm-help.css section 4. */
const WIDE_QUERY = '(min-width: 1000px)';

/* The entry being read is the last one whose top has passed the reading line,
   30% down the window. The observer's box runs from far above the window down
   to that line, so a target counts as passed however far above it has
   scrolled, and a jump (Home, End, a hash) still reports every change. */
const READING_LINE_MARGIN = '1000000px 0px -70% 0px';

/**
 * An observer whose root is this document's own viewport. With the implicit
 * root, browsers ignore rootMargin inside a cross-origin iframe (the theme
 * editor's preview), so the reading line would not apply there. A browser
 * that does not take a Document root throws: it keeps the implicit root,
 * which is the same viewport on the storefront.
 * @param {IntersectionObserverCallback} callback
 * @param {IntersectionObserverInit} options
 * @returns {IntersectionObserver}
 */
const viewportObserver = (callback, options) => {
  try {
    return new IntersectionObserver(callback, { ...options, root: document });
  } catch {
    return new IntersectionObserver(callback, options);
  }
};

/**
 * @typedef {object} PolicySpy
 * @property {HTMLElement} aside
 * @property {HTMLAnchorElement[]} links
 * @property {HTMLElement[]} targets
 * @property {Set<Element>} passed targets whose top is above the reading line
 * @property {IntersectionObserver[]} observers
 * @property {AbortController | null} endWatch scroll listener, only while the last target is wholly in view
 * @property {HTMLAnchorElement | null} current
 * @property {number} frame
 */

class LmPolicyPage extends HTMLElement {
  /** @type {AbortController | null} */
  #controller = null;

  /** @type {PolicySpy | null} */
  #spy = null;

  connectedCallback() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    this.addEventListener('click', (event) => this.#onClick(event), { signal });
    window.addEventListener('hashchange', () => this.#reveal(window.location.hash), { signal });

    const wide = window.matchMedia(WIDE_QUERY);
    const syncSpy = () => (wide.matches ? this.#startSpy() : this.#stopSpy());
    wide.addEventListener('change', syncSpy, { signal });
    syncSpy();

    /* Page opened at /pages/policies/privacy#cookies: open the section now, so the
       browser's own scroll to the fragment lands on open content. Chrome clears focus
       when it processes that fragment, so move focus only after load, and only if the
       visitor has not focused something else in the meantime. */
    const details = detailsForHash(this, window.location.hash);
    if (!details) return;

    details.open = true;
    const focusOnLoad = () => {
      requestAnimationFrame(() => {
        if (document.activeElement === document.body || document.activeElement === null) this.#focusSummary(details);
      });
    };
    if (document.readyState === 'complete') focusOnLoad();
    else window.addEventListener('load', focusOnLoad, { once: true, signal });
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#controller = null;
    this.#stopSpy();
  }

  /* Mark the contents entry being read. Needs the desktop aside and two or more
     entries whose targets exist; otherwise the page stays as it is. */
  #startSpy() {
    if (this.#spy || typeof IntersectionObserver !== 'function') return;

    const aside = this.querySelector('.lm-policy__aside');
    if (!(aside instanceof HTMLElement)) return;

    /** @type {HTMLAnchorElement[]} */
    const links = [];
    /** @type {HTMLElement[]} */
    const targets = [];
    for (const link of aside.querySelectorAll('.lm-policy__contents-list a[href^="#"]')) {
      const target = targetForHash(this, link.getAttribute('href'));
      if (!(link instanceof HTMLAnchorElement) || !target || targets.includes(target)) continue;
      links.push(link);
      targets.push(target);
    }
    if (targets.length < 2) return;

    /** @type {PolicySpy} */
    const spy = { aside, links, targets, passed: new Set(), observers: [], endWatch: null, current: null, frame: 0 };
    const queue = () => {
      if (spy.frame) return;
      spy.frame = requestAnimationFrame(() => {
        spy.frame = 0;
        this.#spyUpdate(spy);
      });
    };

    const line = viewportObserver((entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) spy.passed.add(entry.target);
        else spy.passed.delete(entry.target);
      }
      queue();
    }, { rootMargin: READING_LINE_MARGIN });
    for (const target of targets) line.observe(target);

    /* Near the end of the page the last target (Who we are) may never reach the
       reading line. While it is wholly in view, watch for the end of the page. */
    const end = viewportObserver((entries) => {
      const whole = entries.some((entry) => entry.isIntersecting && entry.intersectionRatio > 0.99);
      if (whole && !spy.endWatch) {
        spy.endWatch = new AbortController();
        window.addEventListener('scroll', queue, { passive: true, signal: spy.endWatch.signal });
      } else if (!whole && spy.endWatch) {
        spy.endWatch.abort();
        spy.endWatch = null;
      }
      queue();
    }, { threshold: [0, 1] });
    end.observe(targets[targets.length - 1]);

    spy.observers = [line, end];
    this.#spy = spy;
  }

  #stopSpy() {
    const spy = this.#spy;
    if (!spy) return;

    this.#spy = null;
    for (const observer of spy.observers) observer.disconnect();
    spy.endWatch?.abort();
    cancelAnimationFrame(spy.frame);
    for (const link of spy.links) link.removeAttribute('aria-current');
  }

  /** @param {PolicySpy} spy */
  #spyUpdate(spy) {
    if (this.#spy !== spy) return;

    let index = 0;
    spy.targets.forEach((target, i) => {
      if (spy.passed.has(target)) index = i;
    });

    /* Scrolled to the end with the last target in view: that is the one being read. */
    const root = document.documentElement;
    if (spy.endWatch && window.scrollY > 0 && window.scrollY + window.innerHeight >= root.scrollHeight - 2) {
      index = spy.targets.length - 1;
    }

    const link = spy.links[index];
    if (link === spy.current) return;

    spy.current?.removeAttribute('aria-current');
    link.setAttribute('aria-current', 'true');
    spy.current = link;
    this.#keepInAside(spy.aside, link, index === 0);
  }

  /**
   * A long contents list scrolls inside the pinned aside: keep the marked entry
   * in its view (with the first entry, the list's heading too). The aside's own
   * scroll only, never the window's.
   * @param {HTMLElement} aside
   * @param {HTMLElement} link
   * @param {boolean} first
   */
  #keepInAside(aside, link, first) {
    if (aside.scrollHeight <= aside.clientHeight) return;
    if (first) {
      aside.scrollTop = 0;
      return;
    }

    const box = aside.getBoundingClientRect();
    const item = link.getBoundingClientRect();
    if (item.top < box.top) aside.scrollTop -= box.top - item.top;
    else if (item.bottom > box.bottom) aside.scrollTop += item.bottom - box.bottom;
  }

  /**
   * A click on the link already in the URL does not fire hashchange, so open the
   * section here as well. The browser still follows the link as usual (URL and scroll).
   * @param {MouseEvent} event
   */
  #onClick(event) {
    /* A modified or middle click opens the link elsewhere (new tab or window):
       leave this page's sections and focus alone. */
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    if (!(event.target instanceof Element)) return;

    const link = event.target.closest('a[href^="#"]');
    if (!link) return;

    const details = detailsForHash(this, link.getAttribute('href'));
    if (!details) return;

    details.open = true;
    requestAnimationFrame(() => this.#focusSummary(details));
  }

  /**
   * The URL hash changed (a link anywhere on the page, or history navigation).
   * @param {string} hash
   */
  #reveal(hash) {
    const details = detailsForHash(this, hash);
    if (!details) return;

    details.open = true;
    this.#focusSummary(details);
  }

  /** @param {HTMLDetailsElement} details */
  #focusSummary(details) {
    const summary = details.querySelector(':scope > summary');
    if (summary instanceof HTMLElement) summary.focus({ preventScroll: true });
  }
}

/* The site's own cookie banner (module lm-consent, in the overlay group): its
   element opens Cookie settings when this event reaches the document. */
const SITE_CONSENT = 'lm-consent';
const SITE_CONSENT_OPEN = 'lm:consent:open';

/**
 * Whether this page has the site's own cookie banner, ready to open: an
 * <lm-consent> element that lm-consent.js has defined, so the event has a
 * listener. It comes before Shopify's banner.
 * @returns {boolean}
 */
const siteConsent = () => customElements.get(SITE_CONSENT) !== undefined && document.querySelector(SITE_CONSENT) !== null;

/**
 * Shopify's cookie banner API, when the store's banner is on and loaded: the
 * fallback on a page without the site's own banner.
 * @returns {{ showPreferences: () => Promise<unknown> } | null}
 */
const cookieBanner = () => {
  const banner = window.privacyBanner;
  return banner && typeof banner.showPreferences === 'function' ? banner : null;
};

class LmCookiePreferences extends HTMLElement {
  /** @type {AbortController | null} */
  #controller = null;

  /** @type {number} */
  #timer = 0;

  connectedCallback() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    this.querySelector('button')?.addEventListener('click', () => this.#open(), { signal });

    if (this.#sync()) return;

    /* Check again once lm-consent is defined, in case its script ever runs
       after this one (today the overlay group comes before the page content),
       and, because Shopify's banner script loads asynchronously, on load and
       for a few seconds; then leave the button hidden. */
    customElements.whenDefined(SITE_CONSENT).then(() => {
      if (!signal.aborted) this.#sync();
    });
    window.addEventListener('load', () => this.#sync(), { once: true, signal });
    let tries = 0;
    this.#timer = window.setInterval(() => {
      tries += 1;
      if (this.#sync() || tries >= 10) this.#stop();
    }, 500);
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#controller = null;
    this.#stop();
  }

  /** @returns {boolean} whether either banner can open (and the button is shown) */
  #sync() {
    const available = siteConsent() || cookieBanner() !== null;
    this.hidden = !available;
    if (available) this.#stop();
    return available;
  }

  #stop() {
    window.clearInterval(this.#timer);
    this.#timer = 0;
  }

  async #open() {
    if (siteConsent()) {
      /* lm-consent gives focus back, on close, to what had it when it opened.
         Safari does not focus a clicked button, so focus it first. */
      this.querySelector('button')?.focus({ preventScroll: true });
      document.dispatchEvent(new Event(SITE_CONSENT_OPEN));
      return;
    }

    const banner = cookieBanner();
    if (!banner) return;

    try {
      await banner.showPreferences();
    } catch (error) {
      console.error('lm-help: the cookie preferences did not open', error);
    }
  }
}

/**
 * The topic code a link asked for (?topic=return), in lower case, or ''.
 * @returns {string}
 */
const requestedTopic = () => {
  const params = new URLSearchParams(window.location.search);
  return (params.get('topic') ?? '').trim().toLowerCase();
};

/* Fields Shopify does not echo back after a failed post (it echoes email and
   message), kept for this tab between sending the form and the error notice. */
const KEPT_STORAGE_KEY = 'lm-help:contact-form';
const KEPT_FIELDS = ['contact[Topic]', 'contact[Order number]', 'contact[name]'];

/**
 * sessionStorage, or null where the browser blocks it (private modes, settings).
 * @returns {Storage | null}
 */
const tabStorage = () => {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
};

class LmContactForm extends HTMLElement {
  /** @type {AbortController | null} */
  #controller = null;

  connectedCallback() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    const form = this.querySelector('form');
    const status = this.querySelector('[data-lm-status]');
    const failed = status instanceof HTMLElement && status.dataset.lmStatus === 'error';

    /* After a failed post, put back what the visitor typed; on any other load
       (a new visit or a sent message) forget it. */
    let topicRestored = false;
    if (failed && form) topicRestored = this.#restore(form);
    else this.#forget();
    if (!topicRestored) this.#preselectTopic();

    /* Capture phase on the wrapper, so the values are kept before any handler on
       the form itself (Shopify's spam check) can stop the event. */
    this.addEventListener('submit', (event) => {
      if (event.target instanceof HTMLFormElement) this.#remember(event.target);
    }, { signal, capture: true });

    if (status instanceof HTMLElement) this.#focusStatus(status, signal);
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#controller = null;
  }

  /* Select the topic whose data-topic matches ?topic=, compared as text (no
     selector built from the URL). Unknown codes, and a topic the visitor already
     changed, are left alone. */
  #preselectTopic() {
    const code = requestedTopic();
    if (!code) return;

    const select = this.querySelector('select[data-lm-topic]');
    if (!(select instanceof HTMLSelectElement)) return;
    if (select.selectedIndex > 0) return;

    for (const option of select.options) {
      if ((option.dataset.topic ?? '').toLowerCase() !== code) continue;
      option.selected = true;
      return;
    }
  }

  /** @param {HTMLFormElement} form */
  #remember(form) {
    /** @type {Record<string, string>} */
    const values = {};
    for (const name of KEPT_FIELDS) {
      const field = form.elements.namedItem(name);
      if (field instanceof HTMLInputElement || field instanceof HTMLSelectElement) values[name] = field.value;
    }

    try {
      tabStorage()?.setItem(KEPT_STORAGE_KEY, JSON.stringify(values));
    } catch {
      /* Storage full or blocked: the form still posts; the fields start again. */
    }
  }

  /**
   * Put the kept values back into the re-rendered form. A topic is restored only
   * if it is still one of the options.
   * @param {HTMLFormElement} form
   * @returns {boolean} whether the topic was restored
   */
  #restore(form) {
    /** @type {unknown} */
    let saved = null;
    try {
      saved = JSON.parse(tabStorage()?.getItem(KEPT_STORAGE_KEY) ?? 'null');
    } catch {
      return false;
    }
    if (!saved || typeof saved !== 'object') return false;

    let topicRestored = false;
    for (const name of KEPT_FIELDS) {
      const value = /** @type {Record<string, unknown>} */ (saved)[name];
      if (typeof value !== 'string' || value === '') continue;

      const field = form.elements.namedItem(name);
      if (field instanceof HTMLSelectElement) {
        const option = [...field.options].find((candidate) => candidate.value === value);
        if (option) {
          option.selected = true;
          topicRestored = true;
        }
      } else if (field instanceof HTMLInputElement) {
        field.value = value;
      }
    }
    return topicRestored;
  }

  #forget() {
    try {
      tabStorage()?.removeItem(KEPT_STORAGE_KEY);
    } catch {
      /* Nothing kept, or storage blocked. */
    }
  }

  /**
   * After a post the page reloads at #contact-form. Chrome resets focus while it
   * processes the fragment, so focus the notice after load, and only if the
   * visitor has not focused something else in the meantime.
   * @param {HTMLElement} status
   * @param {AbortSignal} signal
   */
  #focusStatus(status, signal) {
    const focusStatus = () => {
      requestAnimationFrame(() => {
        const active = document.activeElement;
        if (active && active !== document.body) return;
        status.focus();
      });
    };
    if (document.readyState === 'complete') focusStatus();
    else window.addEventListener('load', focusStatus, { once: true, signal });
  }
}

if (!customElements.get('lm-policy-page')) customElements.define('lm-policy-page', LmPolicyPage);
if (!customElements.get('lm-cookie-preferences')) customElements.define('lm-cookie-preferences', LmCookiePreferences);
if (!customElements.get('lm-contact-form')) customElements.define('lm-contact-form', LmContactForm);
